import React, { useState, useEffect } from 'react';
import API_URL from '../config';
import { formatDateTime } from '../utils/formatDateTime';
import StateBlock from './ui/StateBlock';
import './WorkflowHistory.css';

// Helper: fetch com token JWT automático
function authFetch(url, options = {}) {
  const token = localStorage.getItem('token');
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
}

// #124: nome do Material Symbol por status (cobre legado + valores atuais)
const STATUS_ICON = {
  rascunho: 'edit_note', aberta: 'folder_open', em_analise: 'search', em_revisao: 'search',
  aguardando_aprovacao: 'hourglass_top', aprovada: 'check_circle', aprovado: 'check_circle',
  reprovado: 'cancel', rejeitada: 'cancel', encerrada: 'lock',
};
const STATUS_LABEL = {
  rascunho: 'Rascunho', aberta: 'Aberta', em_analise: 'Em Análise', em_revisao: 'Em Revisão',
  aguardando_aprovacao: 'Aguardando Aprovação', aprovada: 'Aprovada', aprovado: 'Aprovado',
  reprovado: 'Reprovado', rejeitada: 'Rejeitada', encerrada: 'Encerrada',
};

// #87: "gatilhos" (ações, não edição de campo) — exibidos como ação, sem diff de→para
const TRIGGER_LABEL = {
  reanalize_trigger: 'Reanálise com IA (recalculou a análise)',
  reanalize_6m_trigger: 'Reanálise do 6M',
  modo_analise: 'Upgrade para Investigação',
};

// #87: rótulo amigável do campo editado (fallback: o caminho cru)
function labelCampo(campo) {
  if (!campo) return 'campo';
  const map = {
    'causa_raiz.declaracao': 'Causa raiz', 'causa_raiz': 'Causa raiz',
    'verificacao_eficacia.meta': 'Meta de eficácia',
  };
  if (map[campo]) return map[campo];
  let m = campo.match(/^porques\.(\d+)\.resposta$/);
  if (m) return `Porquê ${Number(m[1]) + 1}`;
  m = campo.match(/analise_6m\.(\w+)/);
  if (m) return `6M · ${m[1].replace(/_/g, ' ')}`;
  m = campo.match(/^acoes_corretivas\.(\d+)\.(\w+)/);
  if (m) return `Ação ${Number(m[1]) + 1} · ${m[2]}`;
  return campo;
}

const trunc = (s, n = 140) => {
  const t = (s == null ? '' : String(s));
  return t.length > n ? `${t.slice(0, n)}…` : t;
};

function WorkflowHistory({ ncId }) {
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (ncId && expanded) {
      fetchTrilha();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ncId, expanded]);

  const fetchTrilha = async () => {
    setLoading(true);
    try {
      // #87: trilha consolidada (status + edições humanas). Endpoint aditivo.
      const response = await authFetch(`${API_URL}/api/v2/nc/${ncId}/trilha`);
      const data = await response.json();
      setEventos(data.trilha || []);
    } catch (error) {
      console.error('Erro ao buscar trilha:', error);
      setEventos([]);
    } finally {
      setLoading(false);
    }
  };

  // Decisão 2 Sessão 22: timezone do tenant via formatDateTime
  const formatDate = (dateStr) => formatDateTime(dateStr);
  const toggleExpanded = () => setExpanded(!expanded);

  if (!ncId) return null;

  return (
    <div className="workflow-history-container">
      <div className="history-header" onClick={toggleExpanded}>
        <h4>
          <span className="material-symbols-outlined wh-header-icon">history</span>
          Trilha de auditoria
        </h4>
        <button className="btn-toggle">
          <span className="material-symbols-outlined btn-toggle-icon">
            {expanded ? 'expand_more' : 'chevron_right'}
          </span>
          {expanded ? 'Ocultar' : 'Mostrar'}
        </button>
      </div>

      {expanded && (
        <div className="history-content">
          {loading ? (
            <StateBlock kind="loading" message="Carregando trilha..." />
          ) : eventos.length === 0 ? (
            <StateBlock kind="empty" message="Nenhum evento registrado ainda." />
          ) : (
            <div className="history-timeline">
              {eventos.map((item, index) => {
                const isEdit = item.tipo === 'edicao';
                const trigger = isEdit ? TRIGGER_LABEL[item.campo] : null;
                const last = index === eventos.length - 1;
                return (
                  <div key={`${item.tipo}-${index}`} className="timeline-item">
                    <div className="timeline-marker">
                      <span className={`timeline-icon${isEdit ? ' timeline-icon--edit' : ''}`}>
                        <span className="material-symbols-outlined">
                          {isEdit ? (trigger ? 'autorenew' : 'edit') : (STATUS_ICON[item.status_novo] || 'description')}
                        </span>
                      </span>
                      {!last && <div className="timeline-line"></div>}
                    </div>

                    <div className="timeline-content">
                      <div className="timeline-header">
                        <span className="timeline-status">
                          {isEdit ? (
                            trigger
                              ? <strong>{trigger}</strong>
                              : <>Editou <strong>{labelCampo(item.campo)}</strong></>
                          ) : (
                            <>
                              {item.status_anterior && (
                                <>
                                  {STATUS_LABEL[item.status_anterior] || item.status_anterior}
                                  <span className="timeline-arrow"> → </span>
                                </>
                              )}
                              <strong>{STATUS_LABEL[item.status_novo] || item.status_novo}</strong>
                            </>
                          )}
                        </span>
                        <span className="timeline-date">{formatDate(item.timestamp)}</span>
                      </div>

                      {item.autor && item.autor !== '—' && (
                        <div className="timeline-user">
                          <span className="material-symbols-outlined timeline-inline-icon">person</span>
                          {item.autor}
                        </div>
                      )}

                      {isEdit && !trigger && (item.valor_anterior || item.valor_corrigido) && (
                        <div className="timeline-observation timeline-edit-diff">
                          <span className="material-symbols-outlined timeline-inline-icon">difference</span>
                          <span>
                            <span className="wh-edit-de">{trunc(item.valor_anterior) || '—'}</span>
                            <span className="timeline-arrow"> → </span>
                            <span className="wh-edit-para">{trunc(item.valor_corrigido) || '—'}</span>
                          </span>
                        </div>
                      )}

                      {!isEdit && item.observacao && (
                        <div className="timeline-observation">
                          <span className="material-symbols-outlined timeline-inline-icon">chat</span>
                          {item.observacao}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default WorkflowHistory;
